export type GallerySort = 'newest' | 'oldest';
export type GalleryRange = 'all' | '7d' | '30d' | '90d';

export interface GalleryQueryItem {
  id: string;
  name: string;
  status: string;
  service?: string;
  createdAt: string;
  favourite?: boolean;
  searchText?: string;
}

export interface GalleryQuery {
  search?: string;
  service?: string | null;
  status?: string | null;
  favouritesOnly?: boolean;
  sort?: GallerySort;
  range?: GalleryRange;
}

const RANGE_MS: Record<Exclude<GalleryRange, 'all'>, number> = {
  '7d': 7 * 24 * 60 * 60 * 1000,
  '30d': 30 * 24 * 60 * 60 * 1000,
  '90d': 90 * 24 * 60 * 60 * 1000,
};

export function applyGalleryQuery<T extends GalleryQueryItem>(items: T[], query: GalleryQuery, now = Date.now()): T[] {
  const search = query.search?.trim().toLowerCase() ?? '';
  const range = query.range ?? 'all';
  const filtered = items.filter((item) => {
    if (query.favouritesOnly && !item.favourite) return false;
    if (query.service && item.service !== query.service) return false;
    if (query.status && item.status !== query.status) return false;
    if (range !== 'all') {
      const created = new Date(item.createdAt).getTime();
      if (!Number.isFinite(created) || now - created > RANGE_MS[range]) return false;
    }
    if (!search) return true;
    const haystack = `${item.name} ${item.searchText ?? ''} ${item.status} ${item.service ?? ''}`.toLowerCase();
    return haystack.includes(search);
  });
  const direction = query.sort === 'oldest' ? 1 : -1;
  return filtered.sort((a, b) => {
    const delta = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime();
    return delta * direction;
  });
}

export function pageWindow(page: number, totalPages: number): (number | 'ellipsis')[] {
  if (totalPages <= 1) return [0];
  const pages = new Set<number>([0, totalPages - 1, page]);
  for (let cursor = page - 1; cursor <= page + 1; cursor += 1) {
    if (cursor >= 0 && cursor < totalPages) pages.add(cursor);
  }
  const sorted = [...pages].sort((a, b) => a - b);
  const window: (number | 'ellipsis')[] = [];
  sorted.forEach((value, index) => {
    if (index > 0 && value - sorted[index - 1] > 1) window.push('ellipsis');
    window.push(value);
  });
  return window;
}

export function pageSlice<T>(items: T[], page: number, pageSize: number): T[] {
  const size = Math.max(1, pageSize);
  const start = Math.max(0, page) * size;
  return items.slice(start, start + size);
}
